// Placeholder for tempoissues/components/common/issues/issue-context-menu.tsx
// Please replace this with the actual content from your tempoissues project.
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuCheckboxItem,
  ContextMenu,
} from "@/components/ui/context-menu";
import {
  CircleCheck,
  User as UserIcon,
  Tag,
  Trash2,
  ChevronLeft,
  CircleUserRound,
  CheckIcon,
  MinusIcon,
  Plus,
  CalendarClock,
  ClockFading,
  CircleDashed,
  TriangleDashed,
  X,
  ChevronRight,
  Component,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { toast } from "sonner";
import { User } from "@/types/users";
import { LabelInterface, ColorItem } from "@/types/labels";
import { Priority, NoPriorityIcon } from "@/types/priorities";
import { Status } from "@/types/status";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useSession } from "@clerk/clerk-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ContextMenuPortal } from "@/components/ui/context-menu";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { HalfFilledTriangleIcon } from "@/assets/icons/half-filled-triangle-icon";
import { getPriorityName, defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, ESTIMATE_TO_TSHIRT_SIZE } from "@/types/issues";
import { Calendar } from "@/components/ui/calendar";
import { format, isToday, isTomorrow } from "date-fns";
import { ShortcutHandler } from "@/components/providers/ShortcutProvider";
import { COLOR_LIST } from "@/utils/constants";
import { getAppliedStatusIconForIssues, useActiveProject } from "@/utils/utils";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import {
  createLabelWithRetry,
  sanitizeLabelName,
  validateLabelName,
  getColorValue,
} from "@/utils/labelUtils";
import { UserAvatar } from "../UserAvatar";
import {
  KanbanDefaultStates,
  STATUS_LIST,
} from "@/components/ProjectList/CanvasKanban";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import { STATUS_TYPE_DISPLAY_NAMES } from "../../ProjectConfiguration/config/statusConfig";
import { useContextMenuStore } from "@/store/zustand/contextMenuStore";
import { validateCustomStatus } from "@/components/ProjectConfiguration/utils/validateCustomStatus";
import { isDefaultStatusName } from "@/components/ProjectConfiguration/utils/statusNameUtils";
import { resolveIssueStatus } from "@/utils/filterUtils";

interface IssueContextMenuProps {
  users: User[];
  labels: LabelInterface[];
  priorities?: Priority[];
  status?: Status[];
}

export const IssueContextMenu = React.memo(
  function IssueContextMenu({
    users = [],
    labels = [],
    priorities = defaultPriorities,
    status = [],
  }: IssueContextMenuProps) {
    // Local UI state
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [labelView, setLabelView] = useState<"labels" | "colorSelect">(
      "labels",
    );
    const [newLabelName, setNewLabelName] = useState<string>("");
    const [labelSearch, setLabelSearch] = useState<string>("");
    const labelInputRef = useRef<HTMLInputElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [showDeleteCanvasModal, setShowDeleteCanvasModal] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const [estimateSearch, setEstimateSearch] = useState<string>("");
    const estimateInputRef = useRef<HTMLInputElement>(null);
    const [prioritySearch, setPrioritySearch] = useState<string>("");
    const priorityInputRef = useRef<HTMLInputElement>(null);
    const [statusSearch, setStatusSearch] = useState<string>("");
    const statusInputRef = useRef<HTMLInputElement>(null);
    const [assigneeSearch, setAssigneeSearch] = useState<string>("");
    const assigneeInputRef = useRef<HTMLInputElement>(null);

    const issueIds = useContextMenuStore((state) => state.contextMenuIssueIds);
    const setContextMenuIssueIds = useContextMenuStore(
      (state) => state.setContextMenuIssueIds,
    );

    // Database and store connections
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

    const allIssues = useIssuesStore((state) => state.canvasIdToIssue);
    const createIssueLabel = useIssuesStore((state) => state.createIssueLabel);
    const bulkUpdateIssueStatus = useIssuesStore(
      (state) => state.bulkUpdateIssueStatus,
    );
    const bulkUpdateIssuePriority = useIssuesStore(
      (state) => state.bulkUpdateIssuePriority,
    );
    const bulkUpdateIssueAssignee = useIssuesStore(
      (state) => state.bulkUpdateIssueAssignee,
    );
    const bulkUpdateIssueEstimate = useIssuesStore(
      (state) => state.bulkUpdateIssueEstimate,
    );
    const bulkUpdateIssueDueDate = useIssuesStore(
      (state) => state.bulkUpdateIssueDueDate,
    );
    const bulkUpdateIssueLabel = useIssuesStore(
      (state) => state.bulkUpdateIssueLabel,
    );
    const bulkDeleteIssues = useIssuesStore((state) => state.bulkDeleteIssues);
    const currentProject = useActiveProject();

    const sortedStatuses = useMemo(() => {
      const defaultStatuses = Object.values(KanbanDefaultStates);
      const customProjectStatuses = (currentProject as any)?.statuses || [];

      const result: Status[] = [];

      defaultStatuses.forEach((defaultStatus) => {
        const defaultStatusObj = STATUS_LIST.find(
          (s) => s.name === defaultStatus,
        );
        if (!defaultStatusObj) return;

        const parentType = defaultStatus.toLowerCase().replace(/\s+/g, "-");

        result.push({
          id: defaultStatusObj.id,
          name: defaultStatus,
          icon: defaultStatusObj.icon,
          badgeIcon: defaultStatusObj.badgeIcon,
          type: parentType,
        });

        const customChildren = customProjectStatuses.filter(
          (custom: ProjectStatus) => {
            return custom.type === parentType;
          },
        );

        customChildren.forEach((custom: ProjectStatus) => {
          result.push({
            id: custom.id,
            name: custom.name,
            icon: defaultStatusObj.icon,
            badgeIcon: defaultStatusObj.badgeIcon,
            type: custom.type,
          });
        });
      });

      return result;
    }, [currentProject, allIssues]);

    const issues = issueIds?.map((id) => allIssues[id]);

    const clearSelection = useIssueSelectionStore(
      (state) => state.clearSelection,
    );

    const issueLabelIds = useMemo<string[]>(() => {
      return issues.flatMap(
        (issue) => (issue?.canvas.labels as unknown as string[]) ?? [],
      );
    }, [issues]);

    // Sort and filter labels
    const sortedAndFilteredLabels = useMemo<{
      applied: LabelInterface[];
      available: LabelInterface[];
    }>(() => {
      const filtered = labels.filter((label) => {
        const labelMatches =
          label.label?.toLowerCase().includes(labelSearch.toLowerCase()) ||
          label.name?.toLowerCase().includes(labelSearch.toLowerCase());

        return labelMatches;
      });

      const applied = filtered
        .filter((label) => issueLabelIds.includes(label.id))
        .sort((a, b) =>
          (a.label || "")
            .toLowerCase()
            .localeCompare((b.label || "").toLowerCase()),
        );

      const available = filtered
        .filter((label) => !issueLabelIds.includes(label.id))
        .sort((a, b) =>
          (a.label || "")
            .toLowerCase()
            .localeCompare((b.label || "").toLowerCase()),
        );

      return {
        applied,
        available,
      };
    }, [labels, labelSearch, issueLabelIds]);

    const getLabelIcon = (labelId: string) => {
      if (issueIds.length > 1) {
        return getAppliedStatusIconForIssues(
          issueIds,
          labelId,
          (issue, labelId) =>
            Array.isArray(issue.canvas.labels) &&
            labelId !== null &&
            issue.canvas.labels.includes(labelId),
        );
      }

      const hasLabel = issueLabelIds.includes(labelId);
      if (hasLabel) {
        return <CheckIcon className="size-3.5" color="#F5F5F6" />;
      }
      return null;
    };

    // Add global escape key handler
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          document.body.style.pointerEvents = "";
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }, []);

    const shouldShowCreateButton = useMemo(() => {
      return (
        labelSearch.trim() &&
        sortedAndFilteredLabels.applied.length === 0 &&
        sortedAndFilteredLabels.available.length === 0
      );
    }, [labelSearch, sortedAndFilteredLabels]);

    const shouldShowHelperText = useMemo(() => {
      return labels.length === 0 && !labelSearch.trim();
    }, [labels.length, labelSearch]);

    // Clean up pointer-events style when modal closes
    useEffect(() => {
      if (!showDeleteCanvasModal) {
        // Use requestAnimationFrame to ensure we run after React's updates
        const cleanup = requestAnimationFrame(() => {
          document.body.style.pointerEvents = "";
        });
        return () => cancelAnimationFrame(cleanup);
      }
    }, [showDeleteCanvasModal]);

    // Event handlers
    const handleStatusChange = async (statusId: string) => {
      if (!issueIds.length) return;
      const newStatus = sortedStatuses.find((s) => s.id === statusId);
      if (newStatus) {
        // Store original statuses for potential rollback
        const originalStatuses = issueIds.map((id) => {
          const issue = allIssues[id];
          return {
            issueId: id,
            originalStatus:
              issue?.featurePrd?.status || KanbanDefaultStates.BACKLOG,
          };
        });

        const isDefaultStatus = isDefaultStatusName(newStatus.name);
        bulkUpdateIssueStatus(supabaseClient, issueIds, newStatus.name);
        toast.success(
          `Status of ${issueIds.length} ${
            issueIds.length != 1 ? "issues" : "issue"
          } updated to ${newStatus.name}`,
        );

        // For custom statuses, validate after the update and rollback if needed
        if (!isDefaultStatus && currentProject?.id) {
          const validation = await validateCustomStatus(
            currentProject.id,
            newStatus.name,
            false,
          );

          if (!validation.isValid) {
            // Rollback: Move all issues back to their original statuses
            const rollbackPrds = originalStatuses.map(
              ({ issueId, originalStatus }) => ({
                canvas_id: issueId,
                status: originalStatus,
              }),
            );

            try {
              // Use the same upsert function for rollback
              await bulkUpdateIssueStatus(
                supabaseClient,
                issueIds,
                originalStatuses[0].originalStatus,
              );
              toast.error(
                validation.error ||
                  "Cannot update to this status - it has been deleted or updated. Issues moved back to previous statuses.",
              );
            } catch (rollbackError) {
              console.error("Error rolling back statuses:", rollbackError);
              toast.error(
                "Status validation failed and rollback failed. Please refresh the page.",
              );
            }
          }
        }
      }
    };

    const handlePriorityChange = async (priority: number | undefined) => {
      if (!issueIds.length) return;

      try {
        await bulkUpdateIssuePriority(
          supabaseClient,
          issueIds,
          priority ?? null,
        );
        toast.success(
          priority === undefined
            ? `Priority removed from ${issueIds.length} ${
                issueIds.length != 1 ? "issues" : "issue"
              }`
            : `Priority of ${issueIds.length} ${
                issueIds.length != 1 ? "issues" : "issue"
              } set to ${getPriorityName(priority)}`,
        );
      } catch (error) {
        console.error("Failed to update priority:", error);
        toast.error("Failed to update priority");
      }
    };

    useEffect(() => {
      return () => {
        setLabelView("labels");
        setNewLabelName("");
        setLabelSearch("");
        setEstimateSearch("");
        setPrioritySearch("");
        setStatusSearch("");
        setAssigneeSearch("");
      };
    }, []);

    const handleAssigneeChange = (userId: string | null) => {
      if (!issueIds.length) return;
      const newAssignee = userId
        ? users.find((u) => u.id === userId) || null
        : null;

      bulkUpdateIssueAssignee(supabaseClient, issueIds, newAssignee);
      toast.success(
        newAssignee
          ? `Assigned to ${newAssignee.name || newAssignee.email || "Unknown"}`
          : `Unassigned ${issueIds.length} ${
              issueIds.length != 1 ? "issues" : "issue"
            }`,
      );
    };

    const handleLabelToggle = async (labelId: string) => {
      if (!issueIds.length) return;

      let labelInterface: LabelInterface | null = null;
      if (labelId === null) {
        labelInterface = null;
      } else {
        const label: LabelInterface | undefined = (
          currentProject?.labels as any[]
        )?.find((l) => l.id === labelId);

        if (label) {
          labelInterface = {
            id: label.id,
            label: label.name,
            color: label.color,
            name: label.name,
          };
        }
      }

      const issueLabels = issues.map((issue) => {
        const labelIds = issue.canvas.labels;
        if (Array.isArray(labelIds)) {
          return {
            issueId: issue.canvas.id,
            labels: labelIds,
          };
        } else {
          return {
            issueId: issue.canvas.id,
            labels: [],
          };
        }
      });

      if (labelInterface === null) {
        // set all issues to have no label
        const emptyIssueLabels = issueLabels.map((entry) => {
          return {
            issueId: entry.issueId,
            labels: [],
          };
        });
        bulkUpdateIssueLabel(supabaseClient, emptyIssueLabels);
        return;
      }

      // go through each entry in the issueLabels array
      let allIssuesHaveLabel = true;
      issueLabels.forEach((entry) => {
        const labels = entry.labels;
        let hasLabel = false;

        if (labelId === null && labels.length === 0) {
          hasLabel = true;
        } else {
          labels.forEach((label) => {
            if (label === labelId) {
              hasLabel = true;
            }
          });
        }
        if (!hasLabel) {
          allIssuesHaveLabel = false;
        }
      });

      const modifiedIssueLabels: { issueId: string; labels: string[] }[] = [];

      if (allIssuesHaveLabel) {
        // remove the label from the issues
        issueLabels.forEach((entry) => {
          const issueId = entry.issueId;
          const newLabels = [...entry.labels];
          newLabels.splice(newLabels.indexOf(labelInterface.id), 1);
          modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
        });
      } else {
        // add the label to the issues
        issueLabels.forEach((entry) => {
          const issueId = entry.issueId;
          const newLabels = [...entry.labels];

          // only add the label if it's not already in the list
          if (newLabels.indexOf(labelInterface.id) === -1) {
            newLabels.push(labelInterface.id);
          }
          modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
        });
      }

      // convert modifiedIssueLabels to issueIdToLabelInterfaces using the project labels
      const issueIdToLabelInterfaces: {
        issueId: string;
        labels: LabelInterface[];
      }[] = [];
      modifiedIssueLabels.forEach((entry) => {
        const labels = entry.labels;
        const labelInterfaces: LabelInterface[] = labels
          .map((label) => {
            const labelInterface = (currentProject?.labels as any[])?.find(
              (l) => l.id === label,
            );
            if (labelInterface) {
              return labelInterface;
            }
            return null;
          })
          .filter((label) => label !== null);
        issueIdToLabelInterfaces.push({
          issueId: entry.issueId,
          labels: labelInterfaces,
        });
      });

      bulkUpdateIssueLabel(supabaseClient, issueIdToLabelInterfaces);
    };

    const handleSetDueDate = async (date: Date | null) => {
      if (!issueIds.length) {
        toast.error("Could not update due date");
        return;
      }

      try {
        await bulkUpdateIssueDueDate(
          supabaseClient,
          issueIds,
          date?.toISOString() ?? null,
        );
        if (date === null) {
          toast.success("Due date removed");
        } else if (isToday(date)) {
          toast.success("Due date set to Today");
        } else if (isTomorrow(date)) {
          toast.success("Due date set to Tomorrow");
        } else {
          toast.success(`Due date set to ${format(date, "MMM d")}`);
        }
      } catch (error) {
        console.error("Failed to update due date:", error);
        toast.error("Failed to update due date");
      }
    };

    const handleColorSelect = async (color: ColorItem) => {
      if (!supabaseClient || !issueIds.length) {
        toast.error("Unable to create label. Please try again.");
        return;
      }

      if (!currentProject?.id) {
        toast.error("No project found. Please try again.");
        return;
      }

      // Create a new label with the selected color
      const newLabel: LabelInterface = {
        id: crypto.randomUUID(),
        name: newLabelName,
        color: { value: color.value },
        label: newLabelName, // For backward compatibility
      };

      try {
        await createIssueLabel(supabaseClient, currentProject, newLabel);
        const firstIssue = allIssues[issueIds[0]];
        if (firstIssue) {
          await createLabelWithRetry(
            newLabel,
            currentProject.id,
            supabaseClient,
            bulkUpdateIssueLabel,
            firstIssue.canvas.id,
            firstIssue.canvas.labels || [],
            labels,
          );
        }

        setNewLabelName("");
        setLabelView("labels");
        setLabelSearch("");
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      } catch (error) {
        console.error("Label creation failed:", error);
      }
    };

    const handleSubscribe = () => {
      setIsSubscribed(!isSubscribed);
      toast.success(
        isSubscribed ? "Unsubscribed from issue" : "Subscribed to issue",
      );
    };

    const handleFavorite = () => {
      setIsFavorite(!isFavorite);
      toast.success(
        isFavorite ? "Removed from favorites" : "Added to favorites",
      );
    };

    const { session } = useSession();

    const handleDeleteCanvas = async () => {
      if (!issueIds.length || !session) return;

      const token = await session.getToken();
      if (!token) {
        throw new Error("Failed to get session token");
      }

      await bulkDeleteIssues(supabaseClient, issueIds, token);
      toast.success(
        `Successfully deleted ${issueIds.length} ${
          issueIds.length === 1 ? "issue" : "issues"
        }`,
      );
    };

    // Handle opening the delete modal
    const handleOpenDeleteModal = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      // Store the issue IDs before closing the context menu
      const currentIssueIds =
        useContextMenuStore.getState().contextMenuIssueIds;
      // Force close any open menus
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      // Then open the modal after a short delay
      setTimeout(() => {
        // Restore the issue IDs before opening the modal
        setContextMenuIssueIds(currentIssueIds);
        setShowDeleteCanvasModal(true);
        useProjectModalStore.getState().addProjectModal("delete-canvas-modal");
      }, 50);
    };

    // Handle the delete action
    const handleModalDelete = async () => {
      try {
        await handleDeleteCanvas();
        // Clear selections and context menu state after successful deletion
        clearSelection();
        setContextMenuIssueIds([]);
        setShowDeleteCanvasModal(false);
      } catch (error) {
        console.error("Failed to delete issues:", error);
        toast.error("Failed to delete issues. Please try again.");
        setShowDeleteCanvasModal(false);
      }
    };

    const handleEstimateChange = async (points: number | undefined) => {
      try {
        bulkUpdateIssueEstimate(supabaseClient, issueIds, points ?? null);
        toast.success(
          points === undefined
            ? "Estimate removed"
            : `Estimate set to ${ESTIMATE_TO_TSHIRT_SIZE[points]}`,
        );
      } catch (error) {
        console.error("Failed to update estimate:", error);
        toast.error("Failed to update estimate");
      }
    };

    const filteredEstimates = ESTIMATE_POINTS.filter((points) =>
      points.toString().includes(estimateSearch),
    );

    // Add scrollbar styles
    const scrollbarStyles = `
      .context-menu-scroll::-webkit-scrollbar {
        width: 8px;
      }
      .context-menu-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .context-menu-scroll::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
      }
      .context-menu-scroll::-webkit-scrollbar-thumb:hover {
        background-color: rgba(255, 255, 255, 0.5);
      }
    `;

    const filteredPriorities = useMemo(() => {
      return priorities.filter((priority) => {
        return priority.name
          ?.toLowerCase()
          .includes(prioritySearch.toLowerCase());
      });
    }, [priorities, prioritySearch]);

    const filteredStatuses = useMemo(() => {
      return sortedStatuses.filter((s) => {
        return s.name?.toLowerCase().includes(statusSearch.toLowerCase());
      });
    }, [sortedStatuses, statusSearch]);

    const filteredAssignees = useMemo(() => {
      return users.filter((user) => {
        const searchTerm = assigneeSearch.toLowerCase();
        const nameMatch = user.name?.toLowerCase().includes(searchTerm);
        const emailMatch = user.email?.toLowerCase().includes(searchTerm);
        return nameMatch || emailMatch;
      });
    }, [users, assigneeSearch]);

    const validIssues = useMemo(() => {
      return issues.filter((issue) => issue !== undefined);
    }, [issues]);

    const statusNames = useMemo(
      () => sortedStatuses.map((status) => status.name),
      [sortedStatuses],
    );

    return (
      <>
        <style>{scrollbarStyles}</style>
        <ContextMenuPortal>
          <ShortcutHandler enabled={true}>
            <ContextMenuContent
              className="w-64"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                setLabelView("labels");
                setNewLabelName("");
                setLabelSearch("");
                setEstimateSearch("");
                setPrioritySearch("");
                setStatusSearch("");
                setAssigneeSearch("");
                useProjectModalStore
                  .getState()
                  .removeProjectModal("issue-context-menu");
                // Only clear context menu issue IDs if delete modal is not open
                if (!showDeleteCanvasModal) {
                  setContextMenuIssueIds([]);
                }
              }}
            >
              <ContextMenuGroup>
                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <CircleCheck className="mr-2 size-4" /> Status
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    <div className="px-2 py-1.5">
                      <Input
                        ref={statusInputRef}
                        placeholder="Search statuses..."
                        value={statusSearch}
                        onChange={(e) => setStatusSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 text-sm bg-secondary-n-950 border-0"
                      />
                    </div>
                    <ContextMenuSeparator />
                    <div
                      className="max-h-[256px] overflow-y-auto context-menu-scroll"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                      }}
                    >
                      <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                        Statuses
                      </div>

                      {filteredStatuses.map((s) => {
                        const Icon = s.icon;

                        const allIssuesHaveThisStatus =
                          validIssues.length > 0 &&
                          validIssues.every((issue) => {
                            const resolvedStatus = resolveIssueStatus(
                              issue?.featurePrd?.status?.trim(),
                              statusNames,
                            );
                            return resolvedStatus === s.name?.trim();
                          });

                        const someIssuesHaveThisStatus = validIssues.some(
                          (issue) => {
                            const resolvedStatus = resolveIssueStatus(
                              issue?.featurePrd?.status?.trim(),
                              statusNames,
                            );
                            return resolvedStatus === s.name?.trim();
                          },
                        );

                        return (
                          <ContextMenuItem
                            key={s.id}
                            onClick={() => handleStatusChange(s.id)}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              {s.name === STATUS_TYPE_DISPLAY_NAMES.backlog ? (
                                <div className="pl-[3px] shrink-0">
                                  <CircleDashed className="size-3.5" />
                                </div>
                              ) : (
                                <div className="shrink-0">
                                  <Icon />
                                </div>
                              )}{" "}
                              <span
                                className="truncate max-w-[120px]"
                                title={s.name}
                              >
                                {s.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {allIssuesHaveThisStatus ? (
                                <CheckIcon className="size-4 text-white" />
                              ) : someIssuesHaveThisStatus ? (
                                <MinusIcon className="size-4 text-white" />
                              ) : null}
                            </div>
                          </ContextMenuItem>
                        );
                      })}
                    </div>
                  </ContextMenuSubContent>
                </ContextMenuSub>

                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <NoPriorityIcon className="mr-2 size-4" /> Priority
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    <div className="px-2 py-1.5">
                      <Input
                        ref={priorityInputRef}
                        placeholder="Search priorities..."
                        value={prioritySearch}
                        onChange={(e) => setPrioritySearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 text-sm bg-secondary-n-950 border-0"
                      />
                    </div>
                    <ContextMenuSeparator />

                    <div
                      className="max-h-[200px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                      }}
                    >
                      <ContextMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePriorityChange(undefined);
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <NoPriorityIcon className="w-3.5 h-3.5 shrink-0" />
                            <span>No priority</span>
                          </div>
                          {getAppliedStatusIconForIssues(
                            issueIds,
                            null,
                            (issue, priorityId) =>
                              issue.featurePrd?.priority ===
                              parseInt(priorityId ?? "0"),
                          )}
                        </div>
                      </ContextMenuItem>

                      <ContextMenuSeparator className="w-full" />

                      <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                        Priorities
                      </div>
                      {filteredPriorities.map((option) => (
                        <ContextMenuItem
                          key={option.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePriorityChange(option.id);
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <option.icon className="w-3.5 h-3.5 shrink-0" />
                              <span>{option.name}</span>
                            </div>
                            {getAppliedStatusIconForIssues(
                              issueIds,
                              option.id.toString(),
                              (issue, priorityId) =>
                                issue.featurePrd?.priority ===
                                parseInt(priorityId ?? "0"),
                            )}
                          </div>
                        </ContextMenuItem>
                      ))}
                    </div>
                  </ContextMenuSubContent>
                </ContextMenuSub>

                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <HalfFilledTriangleIcon className="mr-2 size-4" /> Estimate
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    <div className="px-2 py-1.5">
                      <Input
                        ref={estimateInputRef}
                        placeholder="Search estimates..."
                        value={estimateSearch}
                        onChange={(e) => setEstimateSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 text-sm bg-secondary-n-950 border-0"
                      />
                    </div>
                    <ContextMenuSeparator />
                    <div className="overflow-x-hidden">
                      <ContextMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEstimateChange(undefined);
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <TriangleDashed className="w-3.5 h-3.5 shrink-0" />
                            <span>No estimate</span>
                          </div>
                          {getAppliedStatusIconForIssues(
                            issueIds,
                            null,
                            (issue, estimate) =>
                              issue.featurePrd?.estimate ===
                              (estimate ? parseInt(estimate) : null),
                          )}
                        </div>
                      </ContextMenuItem>

                      <ContextMenuSeparator className="w-full" />

                      <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                        Estimates
                      </div>
                      {filteredEstimates.map((points) => (
                        <ContextMenuItem
                          key={points}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEstimateChange(points);
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <HalfFilledTriangleIcon className="w-3.5 h-3.5 shrink-0" />
                              <span>{ESTIMATE_TO_TSHIRT_SIZE[points]}</span>
                            </div>
                            {getAppliedStatusIconForIssues(
                              issueIds,
                              points.toString(),
                              (issue, estimate) =>
                                issue.featurePrd?.estimate ===
                                parseInt(estimate ?? "0"),
                            )}
                          </div>
                        </ContextMenuItem>
                      ))}
                    </div>
                  </ContextMenuSubContent>
                </ContextMenuSub>

                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <UserIcon className="mr-2 size-4" /> Assignee
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    <div className="px-2 py-1.5">
                      <Input
                        ref={assigneeInputRef}
                        placeholder="Search assignees..."
                        value={assigneeSearch}
                        onChange={(e) => setAssigneeSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 text-sm bg-secondary-n-950 border-0"
                      />
                    </div>
                    <ContextMenuSeparator />
                    <div
                      className="max-h-[256px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                      }}
                    >
                      {/* No Assignee Option */}
                      <ContextMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssigneeChange(null);
                        }}
                        className="flex items-center w-full py-[4px] pl-2"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center">
                              <CircleUserRound className="size-5 text-secondary-n-600" />
                            </div>
                            <span>No assignee</span>
                          </div>
                          {getAppliedStatusIconForIssues(
                            issueIds,
                            null,
                            (issue, assigneeId) =>
                              (issue?.canvas.assigned_user_id ?? null) ===
                              assigneeId,
                          )}
                        </div>
                      </ContextMenuItem>

                      {/* Current Assignee Section */}
                      {issues.some(
                        (issue) => issue?.canvas.assigned_user_id,
                      ) && (
                        <>
                          <ContextMenuSeparator />
                          {Array.from(
                            new Set(
                              issues
                                .map((issue) => issue?.canvas.assigned_user_id)
                                .filter(Boolean)
                                .map((assignee) => assignee),
                            ),
                          ).map((assigneeId) => {
                            const assignee = users.find(
                              (u) => u.id === assigneeId,
                            );

                            if (!assignee) return null;

                            // Only show if matches search
                            const searchTerm = assigneeSearch.toLowerCase();
                            const nameMatch = assignee.name
                              ?.toLowerCase()
                              .includes(searchTerm);
                            const emailMatch = assignee.email
                              ?.toLowerCase()
                              .includes(searchTerm);
                            if (searchTerm && !nameMatch && !emailMatch)
                              return null;

                            return (
                              <ContextMenuItem
                                key={assignee.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssigneeChange(assignee.id);
                                }}
                                className="flex items-center w-full py-[4px] pl-2"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 flex items-center justify-center">
                                      <UserAvatar
                                        userName={
                                          assignee.name || assignee.email
                                        }
                                        userId={assignee.id}
                                      />
                                    </div>
                                    <span className="truncate max-w-[100px]">
                                      {assignee.name || assignee.email}
                                    </span>
                                  </div>
                                  {getAppliedStatusIconForIssues(
                                    issueIds,
                                    assignee.id,
                                    (issue, assigneeId) =>
                                      issue.canvas.assigned_user_id ===
                                      assigneeId,
                                  )}
                                </div>
                              </ContextMenuItem>
                            );
                          })}
                        </>
                      )}

                      {/* Team Members Section */}
                      {filteredAssignees.filter(
                        (user) =>
                          !issues.some(
                            (issue) =>
                              issue?.canvas.assigned_user_id === user.id,
                          ),
                      ).length > 0 && (
                        <>
                          <ContextMenuSeparator />
                          <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                            Team members
                          </div>
                          {filteredAssignees
                            .filter(
                              (user) =>
                                !issues.some(
                                  (issue) =>
                                    issue?.canvas.assigned_user_id === user.id,
                                ),
                            )
                            .map((user) => (
                              <ContextMenuItem
                                key={user.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssigneeChange(user.id);
                                }}
                                className="flex items-center w-full py-[4px] pl-2"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 flex items-center justify-center">
                                      <UserAvatar
                                        userName={user.name || user.email}
                                        userId={user.id}
                                      />
                                    </div>
                                    <span className="truncate max-w-[100px]">
                                      {user.name || user.email}
                                    </span>
                                  </div>
                                  {getAppliedStatusIconForIssues(
                                    issueIds,
                                    user.id,
                                    (issue, assigneeId) =>
                                      issue.canvas.assigned_user_id ===
                                      assigneeId,
                                  )}
                                </div>
                              </ContextMenuItem>
                            ))}
                        </>
                      )}
                    </div>
                  </ContextMenuSubContent>
                </ContextMenuSub>

                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <Tag className="mr-2 size-4" /> Labels
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48 p-0">
                    {labelView === "labels" && (
                      <>
                        <div className="p-2">
                          <div className="relative">
                            <Input
                              ref={searchInputRef}
                              placeholder="Search or add labels"
                              value={labelSearch}
                              onChange={(e) => setLabelSearch(e.target.value)}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (
                                  e.key === "Enter" &&
                                  shouldShowCreateButton
                                ) {
                                  e.preventDefault();
                                  const sanitizedQuery =
                                    sanitizeLabelName(labelSearch);
                                  if (validateLabelName(sanitizedQuery)) {
                                    setNewLabelName(sanitizedQuery);
                                    setLabelView("colorSelect");
                                  } else {
                                    toast.error(
                                      "Please enter a valid label name (1-50 characters)",
                                    );
                                  }
                                }
                              }}
                              onKeyUp={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => {
                                // Only prevent default if we're still in the context menu
                                if (
                                  menuRef.current?.contains(
                                    e.relatedTarget as Node,
                                  )
                                ) {
                                  e.preventDefault();
                                  searchInputRef.current?.focus();
                                }
                              }}
                              className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-5"
                            />
                            {labelSearch && (
                              <button
                                onClick={() => setLabelSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="size-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <ContextMenuSeparator className="m-0" />
                        <div
                          className="max-h-[200px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                          style={{
                            scrollbarWidth: "none",
                            scrollbarColor:
                              "rgba(255, 255, 255, 0.3) transparent",
                          }}
                        >
                          {sortedAndFilteredLabels.applied.length > 0 && (
                            <div className="p-1">
                              {sortedAndFilteredLabels.applied.map((label) => {
                                return (
                                  <ContextMenuItem
                                    key={label.id}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleLabelToggle(label.id);
                                    }}
                                    className="flex items-center hover:bg-secondary-n-950!"
                                  >
                                    <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                                      <div className="flex items-center mr-1 gap-1 shrink-0">
                                        <div className="size-5 rounded-sm p-[2px] flex items-center justify-center bg-secondary-n-700 border border-secondary-n-700">
                                          {getLabelIcon(label.id)}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3 ml-3 min-w-0">
                                        <div
                                          className="size-4 rounded-[3px] shrink-0"
                                          style={{
                                            backgroundColor: getColorValue(
                                              label.color,
                                            ),
                                          }}
                                        />
                                        <span className="truncate font-medium">
                                          {label.label || label.name}
                                        </span>
                                      </div>
                                    </div>
                                  </ContextMenuItem>
                                );
                              })}
                            </div>
                          )}
                          {sortedAndFilteredLabels.available.length > 0 && (
                            <div className="p-1">
                              {sortedAndFilteredLabels.applied.length > 0 && (
                                <>
                                  <ContextMenuSeparator />
                                  <div className="px-4">
                                    <div className="text-2xs p-0 font-medium text-secondary-n-400 pt-1">
                                      Labels
                                    </div>
                                  </div>
                                </>
                              )}
                              {sortedAndFilteredLabels.available.map(
                                (label) => {
                                  return (
                                    <div key={label.id}>
                                      <ContextMenuItem
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleLabelToggle(label.id);
                                        }}
                                        className="flex items-center hover:bg-secondary-n-950! group"
                                      >
                                        <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                                          <div className="flex items-center gap-1 shrink-0">
                                            <div className="p-[2px] w-5 h-5 rounded-sm flex items-center justify-center bg-transparent border border-transparent group-hover:border-secondary-n-700 group-hover:bg-secondary-n-950 transition-colors">
                                              {getLabelIcon(label.id)}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3 ml-3 min-w-0">
                                            <div
                                              className="size-4 rounded-[3px] shrink-0"
                                              style={{
                                                backgroundColor: getColorValue(
                                                  label.color,
                                                ),
                                              }}
                                            />
                                            <span className="truncate">
                                              {label.label || label.name}
                                            </span>
                                          </div>
                                        </div>
                                      </ContextMenuItem>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          )}

                          {shouldShowHelperText && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground text-center">
                              Start typing to create a new label
                            </div>
                          )}

                          {shouldShowCreateButton && (
                            <div className="p-1">
                              <ContextMenuItem
                                onSelect={(e) => {
                                  e.preventDefault();
                                  const sanitizedQuery =
                                    sanitizeLabelName(labelSearch);
                                  if (validateLabelName(sanitizedQuery)) {
                                    setNewLabelName(sanitizedQuery);
                                    setLabelView("colorSelect");
                                  } else {
                                    toast.error(
                                      "Please enter a valid label name (1-50 characters)",
                                    );
                                  }
                                }}
                                className="flex items-center justify-center gap-2 text-sm cursor-pointer bg-background-CTA hover:brightness-110! text-white m-1 mt-2 rounded-md"
                              >
                                <Plus className="size-4" />
                                Create new label
                              </ContextMenuItem>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {labelView === "colorSelect" && (
                      <div className="p-1">
                        <ContextMenuItem
                          disabled
                          className="overflow-hidden wrap-break-word"
                        >
                          Pick a color for label
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        {COLOR_LIST.map((color) => (
                          <ContextMenuCheckboxItem
                            key={color.key}
                            onSelect={(e) => {
                              e.preventDefault();
                              handleColorSelect(color);
                              setNewLabelName("");
                              setLabelView("labels");
                              setLabelSearch("");
                            }}
                            className="flex justify-start gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                          >
                            <div
                              className="size-2.5 rounded-full"
                              style={{ backgroundColor: color.value }}
                            />
                            <span>{color.label}</span>
                          </ContextMenuCheckboxItem>
                        ))}
                      </div>
                    )}
                  </ContextMenuSubContent>
                </ContextMenuSub>

                <ContextMenuSub>
                  <ContextMenuSubTrigger className="flex items-center gap-2">
                    <CalendarClock className="size-4" />
                    <span>Due Date</span>
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-auto p-1.5">
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDueDate(null);
                      }}
                      className="px-2 py-1.5"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <ClockFading className="w-3.5 h-3.5 shrink-0" />
                          <span>No due date</span>
                        </div>
                        {getAppliedStatusIconForIssues(
                          issueIds,
                          null,
                          (issue, dueDate) =>
                            issue.featurePrd?.due_date === dueDate,
                        )}
                      </div>
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <div className="p-0">
                      <Calendar
                        mode="single"
                        selected={
                          issues?.length &&
                          issues[0]?.featurePrd?.due_date &&
                          issues[0]?.featurePrd?.due_date !== ""
                            ? new Date(issues[0]?.featurePrd?.due_date)
                            : undefined
                        }
                        onSelect={(date) => handleSetDueDate(date || null)}
                        initialFocus
                      />
                    </div>
                  </ContextMenuSubContent>
                </ContextMenuSub>
                <ContextMenuSeparator />
                <ContextMenuItem
                  className="flex items-center gap-2 text-error-500 hover:bg-error-500/10"
                  onSelect={handleOpenDeleteModal}
                  data-testid="context-menu-delete"
                >
                  <Trash2 className="size-4" />
                  Delete
                </ContextMenuItem>
              </ContextMenuGroup>
            </ContextMenuContent>
          </ShortcutHandler>
        </ContextMenuPortal>
        <AlertDialog
          open={showDeleteCanvasModal}
          onOpenChange={(open) => {
            setShowDeleteCanvasModal(open);
            if (!open) {
              useProjectModalStore
                .getState()
                .removeProjectModal("delete-canvas-modal");
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              {issueIds.length > 1 ? (
                <AlertDialogTitle>
                  Delete {issueIds.length} Canvases
                </AlertDialogTitle>
              ) : (
                <AlertDialogTitle>Delete Canvas</AlertDialogTitle>
              )}
              <AlertDialogDescription>
                {issueIds.length > 1
                  ? "Are you sure you want to delete these canvases? This action cannot be undone."
                  : "Are you sure you want to delete this canvas? This action cannot be undone."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-error-600 text-white hover:bg-error-500"
                onClick={handleModalDelete}
                data-testid="confirm-delete-button"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  },
  (prevProps: IssueContextMenuProps, nextProps: IssueContextMenuProps) => {
    // Custom comparison function for memoization
    return (
      prevProps.users === nextProps.users &&
      prevProps.labels === nextProps.labels &&
      prevProps.priorities === nextProps.priorities &&
      prevProps.status === nextProps.status
    );
  },
);

export default IssueContextMenu;
